import type { ActionableOut, CardOut, SegmentOut, SessionResultOut } from "../contract/api";
import { t } from "../contract/strings";
import { summaryPendingOf } from "../state/store";

/**
 * The result screen's pure rows: what each tab reads from a stored `SessionResultOut`.
 * No React here, so the shared fixtures (`engine/present.test.ts`) and the tabs read the
 * same functions.
 */

/**
 * Mono-caps instrument label, minus the size -- the register every panel on this screen
 * names itself in, composed with whichever type role the spot calls for (`text-label`
 * standard, `text-count` inside chips).
 */
export const LABEL = "font-code font-semibold uppercase tracking-label";

/**
 * The gap between sections on the result screen: between the cards inside a tab, and
 * between the details card and the tab bar. One class, so the two cannot drift apart.
 */
export const SECTION_GAP = "gap-4";

/** A topic chip, as the meeting's topics and each person's draw it. */
export const CHIP =
  "font-code text-count font-semibold tracking-label bg-surface border border-line rounded-pill px-chip-x py-1 text-ink";

/**
 * A 44-unit touch target around a small control without drawing it bigger: an invisible
 * box 44 units tall (`h-11`), centred on the control whatever its own height. A 21-unit
 * chip reaches 11.5 above and below, so rows of chips need 24 units between them for the
 * boxes not to overlap.
 */
export const HIT_44 =
  "relative after:absolute after:inset-x-0 after:top-1/2 after:h-11 after:-translate-y-1/2 after:content-['']";

/**
 * The speaker filter chips' touch box: 32 units tall (`h-8`), centred, the same width as
 * before. Their rows sit 12 units apart, and a 21-unit chip with a 32-unit box reaches
 * 5.4 above and below, so the boxes of adjacent rows never overlap; 32 stays above the
 * 24-unit minimum target (WCAG 2.2 AA).
 */
export const HIT_CHIP =
  "relative after:absolute after:inset-x-0 after:top-1/2 after:h-8 after:-translate-y-1/2 after:content-['']";

/**
 * Durations as a person reads them aloud -- "5m 20s" -- rather than the transcript's
 * `m:ss` axis format. Minutes drop when there are none ("45s"); 59.6s rounds up and
 * carries, because "0m 60s" is not a sentence. The words are the shared `duration.short_*`
 * copy, so both apps read the same.
 */
export function fmtMinSec(seconds: number): string {
  let m = Math.floor(seconds / 60);
  let s = Math.round(seconds % 60);
  if (s === 60) {
    m += 1;
    s = 0;
  }
  return m > 0
    ? t("duration.short_minutes_seconds", { minutes: m, seconds: s })
    : t("duration.short_seconds", { seconds: s });
}

/** Distinct card topics, in card order -- the meeting's topic chips. */
export function resultTopics(result: SessionResultOut | null): string[] {
  if (!result?.cards) return [];
  const set = new Set<string>();
  for (const card of result.cards) {
    for (const topic of card.topics || []) {
      if (topic) set.add(topic);
    }
  }
  return Array.from(set);
}

export type Commitment = ActionableOut & { speaker: string };

/** One card's commitments, each with the speaker it belongs to. */
export function cardCommitments(card: CardOut): Commitment[] {
  return (card.commitments || []).map((c) => ({ ...c, speaker: c.owner || card.speaker }));
}

/** Every commitment, in card order, with the speaker it belongs to. */
export function resultCommitments(result: SessionResultOut | null): Commitment[] {
  if (!result?.cards) return [];
  return result.cards.flatMap(cardCommitments);
}

/**
 * The summary's key points (#772), in order, without blanks. Empty on a meeting
 * summarised before they existed, which then looks exactly as it did.
 */
export function resultHighlights(result: SessionResultOut | null): string[] {
  return (result?.highlights ?? []).filter((text) => text.length > 0);
}

// ---------------------------------------------------------------------------------------
// Due dates on actions (#772)

/** A calendar day: year, month (1-12), day. */
type Ymd = [number, number, number];

/** The calendar day an instant falls on, in `timeZone` (the reader's own when unset). */
function dayOf(instant: Date, timeZone?: string): Ymd {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return [part("year"), part("month"), part("day")];
}

/** `YYYY-MM-DD` as a real calendar day, or `null` (no 30 February). */
function parseDay(text: string | null | undefined): Ymd | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text ?? "");
  if (!match) return null;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const check = new Date(Date.UTC(y, m - 1, d));
  return check.getUTCFullYear() === y && check.getUTCMonth() === m - 1 && check.getUTCDate() === d
    ? [y, m, d]
    : null;
}

/** Whole days from `a` to `b`. */
function daysBetween(a: Ymd, b: Ymd): number {
  return Math.round((Date.UTC(b[0], b[1] - 1, b[2]) - Date.UTC(a[0], a[1] - 1, a[2])) / 86_400_000);
}

/**
 * A calendar day in the reader's locale ("Thu 15 Oct"), with the year only when it is
 * not this year. Formatted in UTC because the day is a calendar date, not an instant:
 * no zone can move it to the day before.
 */
function formatDay(day: Ymd, thisYear: number, locale?: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: "UTC",
    weekday: "short",
    day: "numeric",
    month: "short",
    year: day[0] !== thisYear ? "numeric" : undefined,
  }).format(new Date(Date.UTC(day[0], day[1] - 1, day[2])));
}

/** How an action's timing reads: the words, and the tone they are drawn in. */
export interface DueLabel {
  text: string;
  /** `due` is the accent; `overdue` is the warning tone; `muted` is a done item's past date. */
  tone: "due" | "overdue" | "muted";
}

/**
 * An action's timing, as its row shows it (#772).
 *
 * - A resolved `due_date`, against the reader's own today: "Due today", "Due tomorrow",
 *   "Due Thu 15 Oct". Once past, "Overdue · Thu 15 Oct" while unticked, and a quiet
 *   "Was due Thu 15 Oct" once ticked.
 * - Only the spoken words: "Due {words}", as said.
 * - Neither: `null`.
 *
 * Pure: `now`, the locale and the zone are arguments, so the day boundary can be tested.
 */
export function dueLabel(
  item: { due?: string | null; due_date?: string | null },
  options: { now: Date; done: boolean; locale?: string; timeZone?: string },
): DueLabel | null {
  const { now, done, locale, timeZone } = options;
  const today = dayOf(now, timeZone);
  const date = parseDay(item.due_date);
  if (date) {
    const days = daysBetween(today, date);
    const shown = formatDay(date, today[0], locale);
    if (days < 0) {
      return done
        ? { text: t("result.action_was_due", { date: shown }), tone: "muted" }
        : { text: t("result.action_overdue", { date: shown }), tone: "overdue" };
    }
    if (days === 0) return { text: t("result.action_due_today"), tone: "due" };
    if (days === 1) return { text: t("result.action_due_tomorrow"), tone: "due" };
    return { text: t("result.action_due_on", { date: shown }), tone: "due" };
  }
  // Only the spoken words: shown as said, as before #772. The meeting's date is in the
  // details card above, so the words are not re-anchored here.
  const words = item.due?.trim();
  return words ? { text: t("result.action_due", { due: words }), tone: "due" } : null;
}

/**
 * A card's key quote (#772): the speaker's own words, already checked by the server
 * against their own lines, with the moment it was said. `null` when there is none.
 */
export function cardQuote(card: CardOut): { text: string; source_start: number } | null {
  const quote = card.quote;
  return quote && quote.text ? { text: quote.text, source_start: quote.source_start } : null;
}

/**
 * Real speech seconds, from the two places the pipeline records it: the merge's
 * speech frames (`speech_sec`), or -- for results written before that field
 * existed -- the cards' own airtime. `null` means the run never measured it.
 *
 * The screen no longer shows speaking time (#763); the shared fixtures still carry the
 * number, and this is the web's reading of it.
 */
export function speechSecOf(result: SessionResultOut | null): number | null {
  if (!result) return null;
  if (typeof result.speech_sec === "number" && result.speech_sec > 0) {
    return result.speech_sec;
  }
  const sum = result.cards?.reduce((acc, c) => acc + (c.spoke_sec || 0), 0) ?? 0;
  return sum > 0 ? sum : null;
}

/** One card's share of the total spoken seconds, capped at 100. Fixture parity only. */
export function cardSharePct(card: CardOut, totalSpoken: number): number {
  if (!(totalSpoken > 0)) return 0;
  return Math.min(100, Math.round((card.spoke_sec / totalSpoken) * 100));
}

/** The summary chip's word while it is pending or failed, or `null`. */
export function summaryStatusMessage(result: SessionResultOut): string | null {
  if (summaryPendingOf(result)) return t("result.writing_summary");
  if (result.summary_status === "failed") return t("result.not_available");
  return null;
}

/** The summary body: the stored text, or the sentence that stands in while it lands. */
export function summaryBody(result: SessionResultOut): string | null {
  if (result.summary) return result.summary;
  if (summaryPendingOf(result)) return t("result.writing_the_summary");
  return null;
}

/**
 * The sentence that says a summary failed, when there is no stored text to show. This is
 * the shared row (`result.summary_failed`); the web's tabs say where the transcript is
 * instead, with `summaryFailureInTabs`.
 */
export function summaryFailure(result: SessionResultOut): string | null {
  if (result.summary) return null;
  return result.summary_status === "failed" ? t("result.summary_failed") : null;
}

/** `summaryFailure`, worded for the tabbed screen: the transcript is a tab, not below. */
export function summaryFailureInTabs(result: SessionResultOut): string | null {
  return summaryFailure(result) ? t("result.summary_failed_tab") : null;
}

/**
 * The copy for a result the server would not hand over. A refusal (403, 404) means this
 * account took no part; a 410 means the meeting was closed as abandoned and its audio is
 * gone; anything else, including no connection, is worth another try.
 */
export function resultLoadProblemKey(
  status: number | undefined
): "result.load_no_access" | "result.load_deleted" | "result.load_failed" {
  if (status === 403 || status === 404) return "result.load_no_access";
  if (status === 410) return "result.load_deleted";
  return "result.load_failed";
}

// ---------------------------------------------------------------------------------------
// Tabs

export const RESULT_TABS = ["quick", "detailed", "transcript"] as const;
export type ResultTab = (typeof RESULT_TABS)[number];

/** The `?tab=` value, or Quick for a missing or unknown one. */
export function tabFromParam(value: string | null): ResultTab {
  return (RESULT_TABS as readonly string[]).includes(value ?? "") ? (value as ResultTab) : "quick";
}

// ---------------------------------------------------------------------------------------
// The details line

/** Clock time of day on the viewer's own clock, 12- or 24-hour as the device reads it. */
function clockTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/**
 * The details line's parts: the date, the start–end time and the duration. A result
 * written before `created_at` existed has only the duration.
 */
export function detailsParts(
  createdAt: string | null | undefined,
  durationSec: number
): { date: string | null; time: string | null; duration: string } {
  const duration = fmtMinSec(durationSec);
  if (!createdAt) return { date: null, time: null, duration };
  const start = new Date(createdAt);
  if (Number.isNaN(start.getTime())) return { date: null, time: null, duration };
  const end = new Date(start.getTime() + Math.max(0, durationSec) * 1000);
  return {
    date: start.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }),
    time: t("result.time_range", { start: clockTime(start), end: clockTime(end) }),
    duration,
  };
}

// ---------------------------------------------------------------------------------------
// The transcript: search, the speaker filter, copy and jump

/** Escape a typed query for a `RegExp`, so `(` or `.` search for themselves. */
function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** A case-insensitive matcher for the typed query, or `null` when it is blank. */
export function queryMatcher(query: string): RegExp | null {
  const trimmed = query.trim();
  return trimmed ? new RegExp(escapeRegExp(trimmed), "gi") : null;
}

/**
 * `text` cut into plain and matching runs, in order, for the highlight. Built from the
 * matcher's own indices on the original text, so a letter whose lowercase is longer
 * never shifts the highlight.
 */
export function highlightParts(text: string, query: string): Array<{ text: string; match: boolean }> {
  const matcher = queryMatcher(query);
  if (!matcher || !text) return [{ text, match: false }];
  const parts: Array<{ text: string; match: boolean }> = [];
  let last = 0;
  for (const m of text.matchAll(matcher)) {
    const at = m.index ?? 0;
    if (at > last) parts.push({ text: text.slice(last, at), match: false });
    parts.push({ text: m[0], match: true });
    last = at + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last), match: false });
  return parts.length ? parts : [{ text, match: false }];
}

/** How many times the query occurs in `text`. */
export function countMatches(text: string | null | undefined, query: string): number {
  const matcher = queryMatcher(query);
  if (!matcher || !text) return 0;
  return Array.from(text.matchAll(matcher)).length;
}

/**
 * The turns a search and a speaker filter leave, over every turn of the meeting -- not
 * only the page that is loaded -- with the total number of matches.
 */
export function filterSegments(
  segments: SegmentOut[],
  query: string,
  speaker: string | null
): { segments: SegmentOut[]; matches: number } {
  const searching = queryMatcher(query) !== null;
  let matches = 0;
  const kept = segments.filter((seg) => {
    if (speaker !== null && seg.speaker !== speaker) return false;
    if (!searching) return true;
    const n = countMatches(seg.text, query);
    matches += n;
    return n > 0;
  });
  return { segments: kept, matches };
}

/**
 * The people the speaker filter offers: the cards' order first (the colours' order), then
 * anyone else in order of first appearance in the transcript.
 */
export function transcriptSpeakers(segments: SegmentOut[], speakerOrder: string[]): string[] {
  const present = new Set(segments.map((s) => s.speaker));
  const ordered: string[] = [];
  for (const s of speakerOrder) if (present.has(s) && !ordered.includes(s)) ordered.push(s);
  for (const s of segments) if (!ordered.includes(s.speaker)) ordered.push(s.speaker);
  return ordered;
}

/**
 * The turn a moment belongs to: the one covering it, else the last one that started
 * before it, else the first. `null` only for a meeting with no turns.
 */
export function turnAt(segments: SegmentOut[], time: number): SegmentOut | null {
  if (!segments.length) return null;
  const covering = segments.find((s) => s.start <= time && time < s.end);
  if (covering) return covering;
  let before: SegmentOut | null = null;
  for (const s of segments) if (s.start <= time && (!before || s.start >= before.start)) before = s;
  return before ?? segments.reduce((a, b) => (b.start < a.start ? b : a));
}
